import type { AuthUser, TechnicalUser } from "@/model/user";
import type { LogoutReason } from "@/router/types";
import { useFeedbackStore } from "@/store/feedback";
import { useLicenseStore } from "@/store/license";
import {
  amplitudeApiKey,
  amplitudeDebug,
  amplitudeReverseProxy,
  build,
  environment,
  faroUrl,
  intercomApi,
  intercomId,
  isCloud,
  posthogApiKey,
  posthogFeedbackSurveyId,
  posthogReverseProxy,
} from "@/utils/env/Environment";

import { userLoggedOut } from "./events/other";
import { initAmplitude, resetAmplitude } from "./impl/amplitude";
import { initFaro, logoutFaroUser } from "./impl/faro";
import { initIntercom, resetIntercom } from "./impl/intercom";
import { initPosthog, logoutPosthogUser, startPosthogSessionRecording } from "./impl/posthog";
import { trackEvent, trackUserSettings } from "./track";

const REGISTRATION_RECORDING_SOURCE = "registration";

export function initTracking(authUser: AuthUser, user: TechnicalUser) {
  useFeedbackStore().reset();
  if (isCloud) {
    const userInfo = { ...authUser, ...user };
    const license = useLicenseStore().license;
    if (license.tracking) {
      const licenseInfo = {
        type: license.type,
        plan: license.plan,
        expired: useLicenseStore().isExpired,
      };
      if (amplitudeApiKey) {
        const amplitudeConfig = {
          apiKey: amplitudeApiKey,
          reverseProxy: amplitudeReverseProxy,
          build: build.hash,
          debug: amplitudeDebug,
        };
        void initAmplitude(amplitudeConfig, licenseInfo, userInfo).then(trackUserSettings);
      }
      if (posthogApiKey) {
        const posthogConfig = {
          apiKey: posthogApiKey,
          reverseProxy: posthogReverseProxy,
        };
        void initPosthog(posthogConfig, licenseInfo, userInfo).then((initialized) => {
          if (!initialized) return;
          useFeedbackStore().load(posthogFeedbackSurveyId);
          if (consumeRegistrationRecordingMarker()) {
            startPosthogSessionRecording();
          }
        });
      }
      if (intercomId && intercomApi) {
        const intercomConfig = { id: intercomId, api: intercomApi };
        initIntercom(intercomConfig, licenseInfo, userInfo);
      }
    }
    if (faroUrl) {
      const faroConfig = {
        url: faroUrl,
        samplingRate: 0.08,
        build: build.hash,
        environment,
      };
      void initFaro(faroConfig, userInfo);
    }
  }
}

export function trackLogout(reason: LogoutReason) {
  trackEvent(userLoggedOut(reason));
  resetAmplitude();
  useFeedbackStore().reset();
  logoutPosthogUser();
  logoutFaroUser();
  resetIntercom();
}

function consumeRegistrationRecordingMarker() {
  const url = new URL(window.location.href);
  if (url.searchParams.get("from") !== REGISTRATION_RECORDING_SOURCE) return false;

  url.searchParams.delete("from");
  window.history.replaceState(window.history.state, "", url.toString());
  return true;
}
